import type { AggregatedQto } from "./types";
import { qtoIdFromLineItemNotes } from "./utils";

export interface EstimateLineItemForReview {
  id: string;
  name: string;
  quantity: number;
  uom: string;
  notes: string | null;
}

/** Order column: integration `qtoCustomId` when set; otherwise empty. Always a string so Excel does not coerce numeric-looking ids. */

export function reviewOrderCellValue(item: AggregatedQto): string {
  const v = item.qtoCustomId;
  if (v == null) return "";
  return String(v);
}

/** Order column write value: string formula `="…"` keeps leading zeros without Text format (avoids the green triangle). */

export function reviewOrderCellInput(item: AggregatedQto): string {
  const raw = reviewOrderCellValue(item);
  if (raw === "") return "";
  return `="${raw.replace(/"/g, '""')}"`;
}

/**
 * Find the estimate line item whose `notes` link to this aggregated QTO via
 * `QTO: <qtoId>` (written by `qtoTagForClassification`). Strict id match only:
 * a `(uom, name)` fallback would let new sources collide with line items from
 * other sources or other packages that happen to share both fields.
 */

export function findLinkedLineItem(
  qto: AggregatedQto,
  lineItems: EstimateLineItemForReview[]
): EstimateLineItemForReview | undefined {
  for (const li of lineItems) {
    const fromId = qtoIdFromLineItemNotes(li.notes);
    if (fromId && fromId === qto.qtoId) {
      return li;
    }
  }
  return undefined;
}

export function qtoReviewRowKind(item: AggregatedQto, lineItems: EstimateLineItemForReview[]): "add" | "update" | null {
  if (item.quantity === 0) return null;
  if (!item.uom) return null;
  const linked = findLinkedLineItem(item, lineItems);
  if (!linked) return "add";
  if (Math.round(linked.quantity) !== Math.round(item.quantity)) return "update";
  return null;
}

/** Row counts for QTO Review (same rules as `reviewQTO` sheet rows). */

export function countQtoReviewRows(
  aggregated: AggregatedQto[],
  lineItems: EstimateLineItemForReview[]
): { toAdd: number; toUpdate: number } {
  let toAdd = 0;
  let toUpdate = 0;
  for (const item of aggregated) {
    const kind = qtoReviewRowKind(item, lineItems);
    if (kind === "add") toAdd++;
    else if (kind === "update") toUpdate++;
  }
  return { toAdd, toUpdate };
}

/** Resolved from GET /api/products?search=products.id;IN;<ids> for QTO Review Linked Item cells. */

export const QTO_REVIEW_TYPE_WARN = "WARNING" as const;

/** Comment text on Type (G) for WARN rows (threaded comments). */

export const LINKED_NAME_BROKEN_PRODUCT_ID = "<BROKEN LINK>";

/** Linked item name (column H) when an Add row has no product reference on the classification. */

export const LINKED_NAME_NO_UPC_LINK = "<NO UPC LINK>";

export type QtoReviewWarnKind = "broken_product" | "no_upc_link" | "uom_mismatch";

/**
 * Queue a sync while asking Excel to suspend screen updating until that sync completes.
 * This reduces visible redraw/flicker during multi-step sheet construction.
 */

export type ReviewLinkValue = "" | "Line Item" | "Product" | "UPC Item" | typeof QTO_REVIEW_TYPE_WARN;

/** Legacy sheets used "UPC" in the Link/Type column; normalize when reading. */

export function normalizeReviewLink(raw: string): ReviewLinkValue {
  const t = raw.trim();
  if (t === "Line Item") return "Line Item";
  if (t === "Product" || t === "UPC" || t === "UPC Item") return "Product";
  if (t === QTO_REVIEW_TYPE_WARN) return QTO_REVIEW_TYPE_WARN;
  return "";
}

export function inferWarnKind(link: ReviewLinkValue, lineItemName: string): QtoReviewWarnKind | undefined {
  if (link !== QTO_REVIEW_TYPE_WARN) return undefined;
  const name = lineItemName.trim();
  if (name === LINKED_NAME_BROKEN_PRODUCT_ID || name === "<broken link>") return "broken_product";
  if (name === LINKED_NAME_NO_UPC_LINK) return "no_upc_link";
  return "uom_mismatch";
}

export interface ReviewRow {
  action: "Add" | "Update" | "Ignore";
  /** Sheet column G (Type): blank, "Line Item", "Product", or warn (value equals `QTO_REVIEW_TYPE_WARN` in this module). */
  link: ReviewLinkValue;
  /** When `link` is WARN: distinguishes CSV submit (broken product vs UOM mismatch). */
  warnKind?: QtoReviewWarnKind;
  /** Estimate line item id (hidden column K); used for Update CSV `id`. */
  lineItemId: string;
  /** QTO id (hidden column L); used for CSV notes `QTO: <id>`. */
  qtoId: string;
  /** Ediphi product id when Type is Product (hidden column M). */
  productId: string;
  order: string;
  takeoffName: string;
  takeoffQty: number;
  takeoffUom: string;
  /** Linked Item: line item fields for Update, or display fields for Add+Product. */
  lineItemName: string;
  lineItemQty: number;
  lineItemUom: string;
}

/** Row as read from the QTO Review sheet, with its 1-based Excel row index. */

export type ReviewRowOnSheet = ReviewRow & { excelRow: number };

/**
 * Map sheet row values to Type + ids.
 * v3: Type G (no spacer column); v3-spacer: older layout with blank G and Type H; v2: Link C; legacy: UPC K.
 */

export function parseOrderColumnReviewDataRow(
  row: unknown[],
  sheetLayout: "v3" | "v3-spacer" | "v2" | "legacy"
): { link: ReviewLinkValue; lineItemId: string; qtoId: string; productId: string } {
  if (sheetLayout === "v3" || sheetLayout === "v3-spacer") {
    const o = sheetLayout === "v3-spacer" ? 1 : 0;
    const typeRaw = String(row[5 + o] ?? "").trim();
    const qtoId = String(row[10 + o] ?? "").trim();
    if (qtoId !== "") {
      const link: ReviewLinkValue =
        typeRaw === "Line Item" ||
        typeRaw === "Product" ||
        typeRaw === "UPC" ||
        typeRaw === "UPC Item" ||
        typeRaw === QTO_REVIEW_TYPE_WARN
          ? normalizeReviewLink(typeRaw)
          : "";
      return {
        link,
        lineItemId: String(row[9 + o] ?? "").trim(),
        qtoId,
        productId: String(row[11 + o] ?? "").trim(),
      };
    }
    const classFallback = String(row[10 + o] ?? "").trim();
    if (
      typeRaw === "Line Item" ||
      typeRaw === "UPC" ||
      typeRaw === "Product" ||
      typeRaw === "UPC Item" ||
      typeRaw === QTO_REVIEW_TYPE_WARN
    ) {
      const normalized = normalizeReviewLink(typeRaw);
      return {
        link: normalized,
        lineItemId: "",
        qtoId: classFallback,
        productId:
          normalized === "Product" || normalized === QTO_REVIEW_TYPE_WARN ? String(row[11 + o] ?? "").trim() : "",
      };
    }
    if (typeRaw) {
      return { link: "Line Item", lineItemId: typeRaw, qtoId: classFallback, productId: "" };
    }
    return { link: "", lineItemId: "", qtoId: classFallback, productId: "" };
  }

  if (sheetLayout === "v2") {
    const linkRaw = String(row[1] ?? "").trim();
    const qtoId = String(row[10] ?? "").trim();
    if (qtoId !== "") {
      const link: ReviewLinkValue =
        linkRaw === "Line Item" ||
        linkRaw === "Product" ||
        linkRaw === "UPC" ||
        linkRaw === "UPC Item" ||
        linkRaw === QTO_REVIEW_TYPE_WARN
          ? normalizeReviewLink(linkRaw)
          : "";
      return {
        link,
        lineItemId: String(row[9] ?? "").trim(),
        qtoId,
        productId: String(row[11] ?? "").trim(),
      };
    }
    const classFallback = String(row[10] ?? "").trim();
    if (
      linkRaw === "Line Item" ||
      linkRaw === "UPC" ||
      linkRaw === "Product" ||
      linkRaw === "UPC Item" ||
      linkRaw === QTO_REVIEW_TYPE_WARN
    ) {
      const normalized = normalizeReviewLink(linkRaw);
      return {
        link: normalized,
        lineItemId: "",
        qtoId: classFallback,
        productId: normalized === "Product" || normalized === QTO_REVIEW_TYPE_WARN ? String(row[11] ?? "").trim() : "",
      };
    }
    if (linkRaw) {
      return { link: "Line Item", lineItemId: linkRaw, qtoId: classFallback, productId: "" };
    }
    return { link: "", lineItemId: "", qtoId: classFallback, productId: "" };
  }

  const linkRaw = String(row[1] ?? "").trim();
  const classInM = String(row[11] ?? "").trim() !== "";
  if (classInM) {
    const link: ReviewLinkValue =
      linkRaw === "Line Item" ||
      linkRaw === "UPC" ||
      linkRaw === "Product" ||
      linkRaw === "UPC Item" ||
      linkRaw === QTO_REVIEW_TYPE_WARN
        ? normalizeReviewLink(linkRaw)
        : "";
    return {
      link,
      lineItemId: String(row[10] ?? "").trim(),
      qtoId: String(row[11] ?? "").trim(),
      productId: link === "Product" ? String(row[9] ?? "").trim() : "",
    };
  }

  const qtoId = String(row[10] ?? "").trim();
  if (
    linkRaw === "Line Item" ||
    linkRaw === "UPC" ||
    linkRaw === "Product" ||
    linkRaw === "UPC Item" ||
    linkRaw === QTO_REVIEW_TYPE_WARN
  ) {
    return {
      link: normalizeReviewLink(linkRaw),
      lineItemId: "",
      qtoId,
      productId: normalizeReviewLink(linkRaw) === "Product" ? String(row[9] ?? "").trim() : "",
    };
  }
  if (linkRaw) {
    return { link: "Line Item", lineItemId: linkRaw, qtoId, productId: "" };
  }
  return { link: "", lineItemId: "", qtoId, productId: "" };
}

/**
 * Read QTO Review data rows (v3 B–M, v3-spacer B–N, v2 B–M, or legacy B–J) with optional 1-based `excelRow`.
 * When `visibleOnly` is true, skips rows hidden by AutoFilter (or other row hiding).
 */
